<template>
  <v-dialog
    v-model="dialog"
    persistent
  >
    <template #activator="{ props }">
      <v-btn
        v-bind="props"
        color="secondary"
        dark
        @click="dialog = true"
      >
        <v-icon>mdi-upload</v-icon>
        <span title="Select files for upload">Select files for upload</span>
      </v-btn>
    </template>

    <v-card class="pa-4">
      <v-card-title>Select files to deposit</v-card-title>

      <v-row class="ma-4">
        <v-col>
          <v-table
            v-if="files.length > 0"
            density="compact"
          >
            <thead>
              <tr>
                <th>Name</th>
                <th>Size</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <UploadFileLine
                v-for="item in uploadData"
                :key="item.keyid"
                :keyid="item.keyid"
                :filename="item.name"
                :filesize="item.size"
                :uploading="item.uploading"
                :checksumming="item.checksumming"
                :progress="item.progress"
                :done="item.done"
                :error="item.error"
                @remove-item="removeItem"
              />
            </tbody>
          </v-table>

          <div v-else>
            Click the button below to add files
          </div>

          <input
            id="fileUpload"
            type="file"
            multiple
            hidden
            @change="addFiles($event)"
          >
          <input
            id="folderUpload"
            type="file"
            webkitdirectory
            hidden
            @change="addFolder($event)"
          >
        </v-col>
      </v-row>

      <v-card-actions>
        <v-row>
          <v-col cols="8">
            <v-btn
              color="secondary"
              :disabled="uploading || !client"
              @click="selectFiles()"
            >
              <v-icon>mdi-plus</v-icon>Add files
            </v-btn>
            <v-btn
              color="secondary"
              :disabled="uploading || !client"
              class="ml-2"
              @click="selectFolder()"
            >
              <v-icon>mdi-folder-plus</v-icon>Add folder
            </v-btn>
          </v-col>

          <v-spacer />

          <v-col cols="4">
            <v-btn
              color="red"
              class="ma-3"
              @click="cancelUpload"
            >
              {{ cancelOrDone }}
            </v-btn>
            <v-btn
              color="secondary"
              :disabled="!uploads || uploading"
              class="ma-3"
              @click="uploadAllFiles"
            >
              Upload
            </v-btn>
          </v-col>
        </v-row>
      </v-card-actions>

      <v-row>
        <v-col>
          <v-card-text>
            <b>Guidance Notes</b>
            <ul>
              <li>Please do not close or refresh your browser until the list of files in your deposit has updated, or your upload(s) may fail and need to be restarted</li>
              <li>Uploading very big files and/or large numbers of individual files may take some time depending on the speed of your internet connection, for example:</li>
              <ul>
                <li>100MB @ 10MB/s upload speed = 1m 20s</li>
                <li>1GB @ 10MB/s upload speed = 13m 20s</li>
              </ul>
            </ul>
          </v-card-text>
        </v-col>
      </v-row>
    </v-card>
  </v-dialog>
</template>

<script>
import config from "@/config";
import ShortUniqueId from "short-unique-id";
import { Auth } from "@/utils/amplify-compat";
import {
  S3Client,
  PutObjectCommand,
  CreateMultipartUploadCommand,
  UploadPartCommand,
  CompleteMultipartUploadCommand,
  AbortMultipartUploadCommand,
} from "@aws-sdk/client-s3";
import { Crc32 } from "@aws-crypto/crc32";
import { createSHA256 } from "hash-wasm";
import { filesize } from "filesize";
import UploadFileLine from "@/components/UploadFileLine.vue";
import { emit } from "@/utils/bus";

const uid = new ShortUniqueId();
const minChunkSize = 5 * 1024 * 1024; // S3 minimum part size: 5 MB
const maxParts = 9999;                 // S3 hard limit is 10,000; stay just under

// Returns the smallest chunk size that keeps a file within maxParts parts.
function chunkSizeFor(fileSize) {
  return Math.max(minChunkSize, Math.ceil(fileSize / maxParts));
}

const maxPartConcurrency = 4;
const maxFileConcurrency = 4;

function u8ToBase64(u8) {
  let s = "";
  for (let i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i]);
  return btoa(s);
}

async function crc32Base64(blob) {
  const bytes = new Uint8Array(await blob.arrayBuffer());

  // @aws-crypto/crc32 digest() returns a uint32 NUMBER
  const crc = new Crc32().update(bytes).digest();

  // Convert uint32 -> 4-byte big-endian array (what S3 expects)
  const u8 = new Uint8Array(4);
  u8[0] = (crc >>> 24) & 0xff;
  u8[1] = (crc >>> 16) & 0xff;
  u8[2] = (crc >>> 8) & 0xff;
  u8[3] = crc & 0xff;

  return u8ToBase64(u8);
}

export default {
  components: { UploadFileLine },
  emits: ["cancelled", "refresh"],
  data: () => ({
    identityId: null,
    client: null,
    dialog: false,
    uploading: false,
    currFiles: [],
    files: [],
    uploadData: {},
  }),
  computed: {
    ready() { return !!this.client && !!this.identityId },
    selected() {
      return !(this.currFiles.length > 0 && this.files.length > 0);
    },
    uploads() {
      return this.files.length > 0 && this.ready;
    },
    cancelOrDone() {
      return this.allDone(this.uploadData) ? "Done" : "Cancel";
    },
  },

  async mounted() {
    const creds = await Auth.currentCredentials();
    this.identityId = creds.identityId;
    this.client = new S3Client({
      region: config.region,
      credentials: Auth.makeLiveCredentials(),
    });
  },

  methods: {
    fileSize(size) {
      return filesize(size);
    },

    async runPool(items, limit, worker) {
      const results = new Array(items.length);
      let i = 0;

      const runners = new Array(Math.min(limit, items.length)).fill(0).map(async () => {
        while (i < items.length) {
          const idx = i++;
          results[idx] = await worker(items[idx], idx);
        }
      });

      await Promise.all(runners);
      return results;
    },

    async multipartUploadWithCrc32(file, metadata) {
      const Bucket = config.bucketName;
      const Key = file.key;
      const partSz = chunkSizeFor(file.size);

      let UploadId;

      const partCount = Math.ceil(file.size / partSz);
      const parts = Array.from({ length: partCount }, (_, idx) => {
        const PartNumber = idx + 1;
        const start = idx * partSz;
        const end = Math.min(start + partSz, file.size);
        return { PartNumber, start, end };
      });

      let uploadedBytes = 0;
      const updateProgress = () => {
        this.uploadData[Key].progress = (uploadedBytes / file.size) * 100;
      };

      try {
        // 1) create MPU and explicitly choose CRC32
        const createRes = await this.client.send(
          new CreateMultipartUploadCommand({
            Bucket,
            Key,
            Metadata: metadata,
            ChecksumAlgorithm: "CRC32",
          })
        );

        UploadId = createRes.UploadId;

        // 2) upload parts (limited concurrency)
        const uploadedParts = await this.runPool(parts, maxPartConcurrency, async (p) => {
          const chunk = file.slice(p.start, p.end);
          const ChecksumCRC32 = await crc32Base64(chunk);

          const res = await this.client.send(
            new UploadPartCommand({
              Bucket,
              Key,
              UploadId,
              PartNumber: p.PartNumber,
              Body: chunk,
              ChecksumCRC32,
            })
          );

          uploadedBytes += (p.end - p.start);
          updateProgress();

          return { ETag: res.ETag, PartNumber: p.PartNumber, ChecksumCRC32 };
        });

        uploadedParts.sort((a, b) => a.PartNumber - b.PartNumber);

        // 3) complete
        await this.client.send(
          new CompleteMultipartUploadCommand({
            Bucket,
            Key,
            UploadId,
            MultipartUpload: { Parts: uploadedParts },
          })
        );

        this.uploadData[Key].progress = 100;
      } catch (e) {
        // 4) abort on failure (prevents stranded parts)
        if (UploadId) {
          try {
            await this.client.send(
              new AbortMultipartUploadCommand({ Bucket, Key, UploadId })
            );
          } catch (err) {
            console.warn("Failed to abort multipart upload", err);
          }
        }
        throw e;
      }
    },

    addFiles(event) {
      if (!this.ready) {
        console.warn('S3 client not ready yet.');
        return;
      }
      if (this.allDone(this.uploadData)) this.startOver();

      const selectedFiles = [...event.target.files];
      selectedFiles.forEach((file) => {
        file.key =
          "private/" +
          this.identityId +
          "/" +
          this.getActiveDeposit() +
          "/" +
          this.generatedFilename(file);

        // Vue 3 reactivity: direct assignment is fine
        this.uploadData[file.key] = {
          keyid: file.key,
          name: file.name,
          size: file.size,
          checksumming: false,
          uploading: false,
          progress: 0,
          done: false,
          error: null,
        };
      });

      this.files.push(...selectedFiles);
      document.getElementById("fileUpload").value = "";
    },

    removeItem(keyid) {
      try {
        const idx = this.files.findIndex((item) => item.key === keyid);
        if (idx > -1) {
          this.files.splice(idx, 1);
          delete this.uploadData[keyid];
        } else {
          console.log(`cannot find index ${keyid} in`, this.uploadData);
        }
      } catch (error) {
        console.log(error);
      }
    },

    startOver() {
      this.uploading = false;
      this.currFiles.splice(0);
      this.files.splice(0);
      this.uploadData = {};
    },

    allDone(uploads) {
      return Object.keys(uploads).every(
        (k) => uploads[k].done || uploads[k].error !== null
      );
    },

    noErrors(uploads) {
      return Object.keys(uploads).every((k) => uploads[k].error === null);
    },

    removeUploaded() {
      Object.keys(this.uploadData).forEach((k) => {
        if (this.uploadData[k].done) this.removeItem(this.uploadData[k].keyid);
      });
    },

    cancelUpload() {
      if (this.uploading) {
        console.log("Cancelling uploads not implemented");
      }
      this.startOver();
      this.dialog = false;
      this.$emit("cancelled", true);
    },

    hashChunk(hasher, chunk) {
      const fileReader = new FileReader();
      return new Promise((resolve, reject) => {
        fileReader.onload = async (e) => {
          const view = new Uint8Array(e.target.result);
          hasher.update(view);
          resolve();
        };
        fileReader.onerror = () => reject(fileReader.error);
        fileReader.readAsArrayBuffer(chunk);
      });
    },

    async checksumFile(elem) {
      this.uploadData[elem.key].checksumming = true;

      const hasher = await createSHA256();
      const chunkNumber = Math.floor(elem.size / minChunkSize);

      for (let i = 0; i <= chunkNumber; i++) {
        this.uploadData[elem.key].progress = (i * minChunkSize / elem.size) * 100;
        const chunk = elem.slice(
          minChunkSize * i,
          Math.min(minChunkSize * (i + 1), elem.size)
        );
        await this.hashChunk(hasher, chunk);
      }

      const hash = await hasher.digest();
      return hash;
    },

    generateFilename() {
      return uid.stamp(32);
    },

    generatedFilename(elem) {
      if (!("newFilename" in elem)) {
        const key = this.generateFilename();
        const parts = elem.name.split(".");
        elem.newFilename = parts.length > 1 ? `${key}.${parts.pop()}` : key;
      }
      return elem.newFilename;
    },

    originalFilename(elem) {
      return encodeURIComponent(elem.name);
    },

    async generateMetadata(elem) {
      const meta = {
        originalFilename: this.originalFilename(elem),
        checksum: await this.checksumFile(elem),
      };
      if (elem.webkitRelativePath) {
        meta.originalPath = encodeURIComponent(elem.webkitRelativePath);
      }
      return meta;
    },

    selectFiles() {
      document.getElementById("fileUpload").click();
    },

    selectFolder() {
      document.getElementById("folderUpload").click();
    },

    addFolder(event) {
      if (!this.ready) {
        console.warn('S3 client not ready yet.');
        return;
      }
      if (this.allDone(this.uploadData)) this.startOver();

      const selectedFiles = [...event.target.files];
      selectedFiles.forEach((file) => {
        file.key =
          "private/" +
          this.identityId +
          "/" +
          this.getActiveDeposit() +
          "/" +
          this.generatedFilename(file);

        this.uploadData[file.key] = {
          keyid: file.key,
          name: file.webkitRelativePath || file.name,
          size: file.size,
          checksumming: false,
          uploading: false,
          progress: 0,
          done: false,
          error: null,
        };
      });

      this.files.push(...selectedFiles);
      document.getElementById("folderUpload").value = "";
    },

    async uploadFile(elem) {
      const metadata = await this.generateMetadata(elem);

      this.uploadData[elem.key].checksumming = false;
      this.uploadData[elem.key].uploading = true;
      this.uploadData[elem.key].progress = 0;

      // Small files: simple PutObject
      if (elem.size <= minChunkSize) {
        const bodyBytes = new Uint8Array(await elem.arrayBuffer());

        await this.client.send(
          new PutObjectCommand({
            Bucket: config.bucketName,
            Key: elem.key,
            Body: bodyBytes,
            Metadata: metadata,
          })
        );
        this.uploadData[elem.key].progress = 100;
        return;
      }

      // Large files: multipart with CRC32 per part
      await this.multipartUploadWithCrc32(elem, metadata);
    },

    uploadAllFiles() {
      this.uploading = true;

      this.runPool([...this.files], maxFileConcurrency, (elem) =>
        this.uploadFile(elem)
          .then(() => {
            this.uploadData[elem.key].done = true;
          })
          .catch((e) => {
            this.uploadData[elem.key].error = e;
          })
          .finally(() => {
            this.uploadData[elem.key].uploading = false;
          })

      ).then(() => {
        this.removeUploaded();
        this.uploading = false;
        emit("refresh", true);
        this.$emit("refresh", true);
      });
    },

    getActiveDeposit() {
      return this.$route.path.slice(10); // strip `/deposits/`
    },
  },
};
</script>

<style>

</style>
